/**
 * EmbedMunicipality — versão iframe-ready alinhada com o design
 * tectur.pt / PublicMunicipality (paleta laranja quente + rugged).
 *
 * Query params:
 *   ?theme=dark|light        (default: dark)
 *   ?columns=1|2|3           (default: 3, min 1 max 3)
 *   ?limit=N                 (default: 6, cap 24)
 *   ?showHeader=1|0          (default: 1)
 */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MapPin, ArrowRight, Loader2, Mountain, Building2, Route as RouteIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FRONT = typeof window !== 'undefined' ? window.location.origin : '';

const THEMES = {
  dark: {
    bg: '#0F0D0B',
    bgAlt: '#181410',
    card: '#1F1A16',
    cardHover: '#251F1A',
    line: 'rgba(255,255,255,0.08)',
    ink: '#F2EBDD',
    inkMuted: '#8A7A63',
    brand: '#FF6B1A',
    brandDim: 'rgba(255,107,26,0.14)',
    grain: 'rgba(255,255,255,0.015)',
  },
  light: {
    bg: 'transparent',
    bgAlt: 'transparent',
    card: '#FFFFFF',
    cardHover: '#FFF9F3',
    line: 'rgba(15,13,11,0.10)',
    ink: '#0F0D0B',
    inkMuted: '#6B6357',
    brand: '#FF6B1A',
    brandDim: 'rgba(255,107,26,0.10)',
    grain: 'transparent',
  },
};

const fontStack = "'Inter', system-ui, -apple-system, sans-serif";
const kmFmt = (n) => { const v = Number(n) || 0; return v < 1 ? v.toFixed(2) : v.toFixed(1); };

/** Card de rota — cover 16:9 dominante, overlay orange, badge dificuldade,
 *  stats icons pill, CTA "Ver Rota" com seta e hover elevação. */
const RouteCard = ({ route, C }) => {
  const [hover, setHover] = useState(false);
  return (
    <a
      href={`${FRONT}/r/${route.route_id}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`embed-route-card-${route.route_id}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'block', textDecoration: 'none', color: C.ink,
        backgroundColor: hover ? C.cardHover : C.card, borderRadius: 12, overflow: 'hidden',
        border: `1px solid ${hover ? C.brand : C.line}`,
        transition: 'transform .25s cubic-bezier(.2,.9,.3,1.4), box-shadow .25s, border-color .25s, background-color .25s',
        transform: hover ? 'translateY(-6px)' : 'translateY(0)',
        boxShadow: hover ? `0 20px 40px -12px ${C.brand}40, 0 8px 16px -8px rgba(0,0,0,.4)` : '0 2px 8px rgba(0,0,0,.15)',
      }}
    >
      {/* Cover 16:9 com gradient overlay + badge dificuldade */}
      <div style={{
        aspectRatio: '16 / 9',
        background: route.cover_image_path
          ? `linear-gradient(rgba(15,13,11,.05), rgba(15,13,11,.75)), url(${API}/routes/${route.route_id}/cover) center/cover`
          : `linear-gradient(135deg, #3D362E 0%, #1A1613 100%)`,
        position: 'relative',
        borderBottom: `1px solid ${C.line}`,
      }}>
        {!route.cover_image_path && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,.08)' }}>
            <Mountain size={48} />
          </div>
        )}
        {route.difficulty && (
          <span style={{
            position: 'absolute', top: 10, right: 10,
            padding: '5px 10px', borderRadius: 4,
            backgroundColor: C.brand, color: '#fff', fontSize: 9, fontWeight: 900,
            letterSpacing: '1.2px', textTransform: 'uppercase',
            boxShadow: '0 4px 8px rgba(0,0,0,.25)',
          }}>
            {route.difficulty}
          </span>
        )}
        {/* Nome da rota em overlay bottom-left, tectur.pt style */}
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 0,
          padding: '10px 14px 12px',
        }}>
          <h3 style={{
            margin: 0, fontSize: 15, fontWeight: 900,
            color: '#fff', letterSpacing: '-.2px', lineHeight: 1.15,
            textShadow: '0 2px 8px rgba(0,0,0,.6)',
          }}>
            {route.name}
          </h3>
        </div>
      </div>

      {/* Meta stats + CTA */}
      <div style={{ padding: '12px 14px 14px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {route.distance_km != null && (
            <span style={statPillStyle(C)}>
              <RouteIcon size={10} style={{ color: C.brand }} />
              {kmFmt(route.distance_km)} km
            </span>
          )}
          {route.elevation_gain_m > 0 && (
            <span style={statPillStyle(C)}>
              <Mountain size={10} style={{ color: C.brand }} />
              +{Math.round(route.elevation_gain_m)}m
            </span>
          )}
          {route.pois_count > 0 && (
            <span style={statPillStyle(C)}>
              <MapPin size={10} style={{ color: C.brand }} />
              {route.pois_count} POI{route.pois_count === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          color: C.brand, fontSize: 11, fontWeight: 900,
          textTransform: 'uppercase', letterSpacing: '1px',
          borderBottom: `1px solid ${hover ? C.brand : 'transparent'}`,
          paddingBottom: 1,
          transition: 'border-color .2s',
        }}>
          Ver Rota
          <ArrowRight size={12} style={{ transform: hover ? 'translateX(3px)' : 'translateX(0)', transition: 'transform .2s' }} />
        </span>
      </div>
    </a>
  );
};

const statPillStyle = (C) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '3px 8px', borderRadius: 3,
  backgroundColor: C.brandDim, color: C.ink,
  fontSize: 10, fontWeight: 700, letterSpacing: '.3px',
});

const EmbedMunicipality = () => {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const theme = params.get('theme') === 'light' ? 'light' : 'dark';
  const columns = Math.max(1, Math.min(3, parseInt(params.get('columns') || '3', 10)));
  const limit = Math.max(1, Math.min(24, parseInt(params.get('limit') || '6', 10)));
  const showHeader = params.get('showHeader') !== '0';
  const C = THEMES[theme];

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/municipalities/${slug}`);
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) setError(err.response?.status === 404 ? 'Município não encontrado' : 'Erro a carregar');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    const bodyStyle = document.body.style;
    const htmlStyle = document.documentElement.style;
    const prev = { bodyBg: bodyStyle.background, htmlBg: htmlStyle.background, margin: bodyStyle.margin };
    bodyStyle.background = C.bg;
    htmlStyle.background = C.bg;
    htmlStyle.colorScheme = theme;
    bodyStyle.margin = '0';
    return () => {
      bodyStyle.background = prev.bodyBg;
      htmlStyle.background = prev.htmlBg;
      bodyStyle.margin = prev.margin;
    };
  }, [C.bg, theme]);

  if (loading) {
    return (
      <div style={{ minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: C.bg }}>
        <Loader2 style={{ width: 28, height: 28, color: C.brand, animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: 240, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 24, backgroundColor: C.bg, color: C.ink, fontFamily: fontStack }}>
        <Mountain size={40} style={{ color: C.inkMuted }} />
        <p style={{ fontSize: 14, margin: 0, fontWeight: 700 }}>{error}</p>
      </div>
    );
  }

  const routes = (data.routes || []).slice(0, limit);
  const gridColumns = `repeat(${columns}, minmax(0, 1fr))`;

  return (
    <div
      style={{
        backgroundColor: C.bg,
        color: C.ink,
        fontFamily: fontStack,
        padding: '20px 18px 16px',
        // Subtle noise texture — só no dark
        backgroundImage: theme === 'dark'
          ? `radial-gradient(circle at 30% 20%, ${C.grain} 1px, transparent 1px), radial-gradient(circle at 70% 80%, ${C.grain} 1px, transparent 1px)`
          : 'none',
        backgroundSize: '4px 4px, 6px 6px',
      }}
      data-testid="embed-municipality-widget"
    >
      {showHeader && (
        <div style={{ marginBottom: 20 }}>
          {/* Pretitle rugged style */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            marginBottom: 8, flexWrap: 'wrap',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '4px 10px', borderRadius: 3,
              backgroundColor: C.brandDim,
              border: `1px solid ${C.brand}`,
            }}>
              {data.has_logo ? (
                <img
                  src={`${API}/municipalities/${data.municipality_id}/logo`}
                  alt=""
                  style={{ width: 16, height: 16, objectFit: 'contain' }}
                />
              ) : (
                <Building2 size={12} style={{ color: C.brand }} />
              )}
              <span style={{
                fontSize: 9, fontWeight: 900, color: C.brand,
                letterSpacing: '1.5px', textTransform: 'uppercase',
              }}>
                OFICIAL · TECTUR 4×4
              </span>
            </div>
          </div>

          <div style={{
            display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap',
          }}>
            <div>
              <h2 style={{
                margin: 0, fontSize: 22, fontWeight: 900,
                letterSpacing: '-.4px', lineHeight: 1.05,
              }}>
                Rotas 4×4 em <span style={{ color: C.brand }}>{data.name}</span>
              </h2>
              <div style={{
                width: 32, height: 3, backgroundColor: C.brand,
                marginTop: 8, borderRadius: 2,
              }} />
            </div>
            <a
              href={`${FRONT}/m/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="embed-see-all-link"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                color: C.brand, fontSize: 10, fontWeight: 900,
                textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '1px',
                padding: '6px 10px', borderRadius: 999,
                border: `1.5px solid ${C.brand}`,
              }}
            >
              Ver todas <ArrowRight size={11} />
            </a>
          </div>
        </div>
      )}

      {routes.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '40px 16px',
          color: C.inkMuted, fontSize: 13, fontWeight: 600,
        }}>
          <RouteIcon size={40} style={{ opacity: .35, marginBottom: 8 }} />
          <div>Ainda sem rotas públicas neste município.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: gridColumns, gap: 14 }}>
          {routes.map((r) => <RouteCard key={r.route_id} route={r} C={C} />)}
        </div>
      )}

      {/* Powered by pill */}
      <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
        <a
          href="https://tectur.pt/"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 10px', borderRadius: 999,
            backgroundColor: C.brandDim,
            color: C.brand, textDecoration: 'none',
            fontSize: 9, fontWeight: 900, letterSpacing: '1.5px', textTransform: 'uppercase',
            border: `1px solid ${C.line}`,
          }}
        >
          <Mountain size={10} />
          Powered by Tectur 4×4
        </a>
      </div>
    </div>
  );
};

export default EmbedMunicipality;
